import { queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useLayoutEffect } from 'react';
import { FluxContext } from '@/core/flux.context';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { isStoredTableSearch } from '@/itemFinder/itemFinder.utilities';
import { getTableDefinitionStore, getTableEditorStore } from '@/tableDefinitionEditor/store.utilities';
import { getColumnDisplayName } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { useApiGetStoredTableSearchFailuresQuery } from '@seeqdev/api/endpoint/ItemsApi';
import { GetTableDefinitionQueryKey, sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import type { QueryClient } from '@tanstack/react-query';
import type { ColumnDefinition, EditorType } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

/**
 * A table definition, or nothing to fetch without an id. Cached indefinitely: every change publishes its own result, so
 * a staleness timer has nothing to catch.
 */
export const tableDefinitionQueryOptions = (tableDefinitionId: string | undefined) =>
  queryOptions({
    queryKey: GetTableDefinitionQueryKey({ id: tableDefinitionId ?? '' }),
    queryFn: tableDefinitionId
      ? () => sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId }).then(({ data }) => data)
      : skipToken,
    staleTime: Infinity,
  });

/** Reads a table definition from the backend. Pass a `select` to subscribe to only the slice a component needs. */
export function useTableDefinitionQuery<TData = TableDefinitionOutputV1>(
  tableDefinitionId: string | undefined,
  select?: (definition: TableDefinitionOutputV1) => TData,
) {
  return useQuery({ ...tableDefinitionQueryOptions(tableDefinitionId), select });
}

/** Publishes a definition the caller already has, so readers update without a round trip. */
export const cacheTableDefinition = (queryClient: QueryClient, definition: TableDefinitionOutputV1): void => {
  queryClient.setQueryData(tableDefinitionQueryOptions(definition.id).queryKey, definition);
};

/** Finds a column by id and resolves the display name the editor shows for it. */
export const selectColumnById =
  (columnId: string | undefined) =>
  (definition: TableDefinitionOutputV1): ColumnDefinition | undefined => {
    if (!columnId) {
      return undefined;
    }
    const column = definition.columnDefinitions.find((columnDefinition) => columnDefinition.id === columnId);
    return column
      ? { ...column, displayName: getColumnDisplayName(column.columnName, definition.uniqueIDColumnName) }
      : undefined;
  };

/** The id of the table open in the given editor; the key for every table-scoped query the editor makes. */
export const useEditorTableId = (editorType: EditorType): string | undefined =>
  useFluxPath(getTableEditorStore(editorType), (store) => store.tableId) || undefined;

/** A column of the table open in the given editor, resolved from the backend table definition. */
export function useTableColumn(editorType: EditorType, columnId: string | undefined): ColumnDefinition | undefined {
  return useTableDefinitionQuery(useEditorTableId(editorType), selectColumnById(columnId)).data;
}

/** The column currently open in the editor's column configuration. */
export function useColumnToEdit(editorType: EditorType): ColumnDefinition | undefined {
  const columnToEditId = useFluxPath(getTableEditorStore(editorType), (store) => store.columnToEditId);
  return useTableColumn(editorType, columnToEditId);
}

const CHILD_LIMIT_EXCEEDED_WARNING = 'ITEM_FINDER.CHILD_LIMIT_EXCEEDED';

/** Whether the last item finder run for the open table stopped collecting children because it hit the limit. */
export const useChildLimitExceeded = (editorType: EditorType): boolean =>
  useTableDefinitionQuery(
    useEditorTableId(editorType),
    (definition) => !!definition.itemFinder?.lastRunWarnings?.includes(CHILD_LIMIT_EXCEEDED_WARNING),
  ).data ?? false;

/**
 * Whether the open table's item finder searches stored tables and any of those searches failed on its last run.
 *
 * A failed or still-loading request counts as no failures. The failures are fetched once per run by `fetchStoredTableWarnings`, which publishes them to
 * this query's cache entry, so the cached result is not refetched here.
 */
export function useHasStoredTableSearchFailures(editorType: EditorType): boolean {
  const itemFinder = useTableDefinitionQuery(useEditorTableId(editorType), (definition) => definition.itemFinder).data;
  const searchesStoredTables = !!itemFinder?.searches.some((search) => isStoredTableSearch(search));
  const { data } = useApiGetStoredTableSearchFailuresQuery(
    { id: itemFinder?.id ?? '' },
    { enabled: !!itemFinder && searchesStoredTables, staleTime: Infinity },
  );
  return searchesStoredTables && (data?.storedTableSearchFailures.length ?? 0) > 0;
}

/** Whether to show the stored table search failures: there are some and the user has not dismissed them. */
export function useShowStoredTableSearchWarning(editorType: EditorType): boolean {
  const hasFailures = useHasStoredTableSearchFailures(editorType);
  const dismissed = useFluxPath(getTableEditorStore(editorType), (store) => store.storedTableSearchWarningDismissed);
  return hasFailures && !dismissed;
}

/**
 * Keys the column configuration form. The form seeds its state from the column to edit once, when it mounts, so the key
 * changes when that column resolves and the form remounts with it rather than keeping the empty state it started with.
 */
export const columnConfigurationKey = (
  columnToEdit: ColumnDefinition | undefined,
  ...parts: (string | number | undefined)[]
): string => [columnToEdit?.id || 'new', columnToEdit ? 'resolved' : 'unresolved', ...parts].join('_');

/**
 * Transitional bridge: mirrors the legacy table definition store into the query cache so query-backed readers see the
 * same socket-driven updates. Delete once the table definition store is itself backed by React Query.
 *
 * The cache is written from a store listener, not from a render effect: an action often changes the definition and opens
 * a reader of the new definition in the same batch (saving a new column opens it for editing), and that reader renders
 * before any effect of this component runs. The layout effect seeds the definition already in the store before child
 * queries subscribe, so opening the editor does not trigger a duplicate fetch.
 */
// TODO: CRAB-55131 remove need to sync once table definition stores are converted to Zustand
export function useSyncTableDefinitionQueryCache(editorType: EditorType): void {
  const flux = useContext(FluxContext);
  const queryClient = useQueryClient();

  useLayoutEffect(() => {
    const tableDefinitionStore = getTableDefinitionStore(editorType);
    const publishStoreDefinition = () => {
      const tableDefinitionOutput = tableDefinitionStore.tableDefinitionOutput;
      if (tableDefinitionOutput?.id) {
        cacheTableDefinition(queryClient, tableDefinitionOutput);
      }
    };

    publishStoreDefinition();
    const useEvalAsync = false;
    return flux.listenTo(tableDefinitionStore, ['tableDefinitionOutput'], publishStoreDefinition, useEvalAsync);
  }, [flux, queryClient, editorType]);
}
